import { COPY } from '@/shared/constants/copies';
import { ISFormEditDetailsResponse, TFormWidget } from '@/shared/services/forms/forms.types';

import {
	ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP,
	FILE_TYPE_IDS,
	FORM_WIDGET_APPLICATIONS_THRESHOLD,
	FORM_WIDGET_RESPONSE_RATE_THRESHOLD,
} from '@/utils/constants';
import { getDayMonthFromUTC } from '@/utils/date';

import { IAddFieldDropdownOption } from '../../types/form-editing-types';

export { searchCities } from '@/shared/utils/cities';

export const STATIC_FORM_ID = 'create';

export const STATIC_FORM_DATA: ISFormEditDetailsResponse = {
	id: STATIC_FORM_ID,
	name: 'Title',
	description: '',
	status: 'DRAFT',
	inputFields: [
		{
			id: 'static-name-field',
			type: 'SHORT_TEXT',
			question: 'Name',
			required: true,
			preDefinedInputType: 'NAME',
			valid: true,
		},
		{
			id: 'static-email-field',
			type: 'SHORT_TEXT',
			question: 'Email',
			required: true,
			preDefinedInputType: 'EMAIL',
			valid: true,
		},
	],
	logo: '',
	multipleSubmissions: false,
	showOnPublicPage: true,
	emailVerificationRequired: false,
	isCreator: true,
	includedWidgets: ['LOCATION', 'RESPONSE_PERCENTAGE'],
	formCloseMessage: '',
	formSuccessMessage: '',
};

export const FIRST_FORM_JD_TEMPLATE = `<p>This is a space for writing the job description. [Try editing me]</p>
<p></p>
<p>
  Once you are done editing the form, just publish it on socials to start
  receiving submissions. Some features you might find interesting: <br />
</p>
<ul class="list-disc leading-6 mt-2">
  <li>
    <p>
      <strong>Widgets: </strong>You might see a live response rate widget on
      the top. This widget will automatically start showing up to people when
      your response rate crosses 80%. You can turn it off or add other widgets
      to your form.
    </p>
  </li>
  <li>
    <p>
      <strong>Video Question: </strong>You will see a video question below where
      people can record a short 60 second video directly from their front
      camera. To experience it, just go to the preview mode from the top right.
    </p>
  </li>
  <li>
    <p>
      <strong>Daily Brief:</strong> Every morning, you will get an email
      summary of the number of submissions you received the previous day. You
      can enable or disable this feature by clicking on the settings icon on the
      top right.
    </p>
  </li>
</ul>
<p></p>
<p>Below, you can edit/add questions to your form.</p>`;

export const STATIC_FIRST_FORM: ISFormEditDetailsResponse = {
	...STATIC_FORM_DATA,
	name: 'Hiring Form',
	description: FIRST_FORM_JD_TEMPLATE,
	inputFields: [
		...(STATIC_FORM_DATA.inputFields || []),
		{
			id: 'static-linkedin-field',
			type: 'LINK',
			preDefinedInputType: 'LINKEDIN',
			question: 'LinkedIn profile',
			required: true,
			valid: true,
		},
		{
			id: 'static-video-field',
			type: 'VIDEO',
			question: 'What excites you about this role?',
			required: false,
			fileTypes: ['MP4', 'WEBM'],
			description:
				'Please record a short <1 min video below. We care less about the camera, lighting, etc and more about what you say.',
			valid: true,
		},
		{
			id: 'static-long-question-field',
			type: 'LONG_TEXT',
			question: 'What work of yours are you the most proud of?',
			required: true,
			valid: true,
		},
	],
	includedWidgets: ['LOCATION', 'RESPONSE_PERCENTAGE'],
};

export const ADD_FIELD_SUB_OPTIONS_MAP_WITH_OPTION_NAME: {
	[key: string]: IAddFieldDropdownOption[];
} = {
	[ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.OTHER_LINKS]: [
		{
			name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.TWITTER,
			inputFieldType: 'LINK',
			preDefinedInputType: 'TWITTER',
			defaultQuestion: 'X (Twitter) profile',
		},
		{
			name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.INSTAGRAM,
			inputFieldType: 'LINK',
			preDefinedInputType: 'INSTAGRAM',
			defaultQuestion: 'Instagram profile',
		},
		{
			name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.GITHUB,
			inputFieldType: 'LINK',
			preDefinedInputType: 'GITHUB',
			defaultQuestion: 'Github',
		},
		{
			name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.YOUTUBE,
			inputFieldType: 'LINK',
			preDefinedInputType: 'YOUTUBE',
			defaultQuestion: 'YouTube video link',
			defaultDescription:
				'Please make sure that the video link you provide is either a public or unlisted YouTube video. If the YouTube video is private, we will not be able to access it.',
		},
		{
			name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.LINK,
			inputFieldType: 'LINK',
			defaultQuestion: 'Link',
		},
	],
	[ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.SELECTION_FIELDS]: [
		{
			name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.MULTIPLE_CHOICE,
			inputFieldType: 'SINGLE_SELECT',
			inputFieldOptions: ['Option 1'],
		},
		{
			name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.DROPDOWN,
			inputFieldType: 'DROPDOWN',
			inputFieldOptions: ['Option 1'],
		},
		{
			name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.CHECKBOX,
			inputFieldType: 'MULTI_SELECT',
			inputFieldOptions: ['Option 1'],
		},
	],
};

export const ADD_FIELD_DROPDOWN_LIST: IAddFieldDropdownOption[] = [
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.SHORT_ANSWER,
		inputFieldType: 'SHORT_TEXT',
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.LONG_ANSWER,
		inputFieldType: 'LONG_TEXT',
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.NUMBER,
		inputFieldType: 'NUMBER',
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.SELECTION_FIELDS,
		subOptions:
			ADD_FIELD_SUB_OPTIONS_MAP_WITH_OPTION_NAME[
				ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.SELECTION_FIELDS
			],
		withBottomBorder: true,
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.PHONE,
		inputFieldType: 'PHONE_NUMBER',
		defaultQuestion: 'Phone',
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.LINKEDIN,
		inputFieldType: 'LINK',
		preDefinedInputType: 'LINKEDIN',
		defaultQuestion: 'LinkedIn profile',
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.OTHER_LINKS,
		subOptions:
			ADD_FIELD_SUB_OPTIONS_MAP_WITH_OPTION_NAME[ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.OTHER_LINKS],
		withBottomBorder: true,
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.DATE,
		inputFieldType: 'DATE',
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.FILE_UPLOAD,
		inputFieldType: 'FILE_UPLOAD',
		inputFieldFileTypes: FILE_TYPE_IDS,
	},
	{
		name: ADD_FIELD_DROPDOWN_OPTION_NAMES_MAP.VIDEO,
		inputFieldType: 'VIDEO',
		inputFieldFileTypes: ['MP4', 'WEBM'],
		defaultQuestion: 'Record a short video of yourself',
		defaultDescription: '',
	},
];

export const FORM_WIDGET_INFO: Record<
	TFormWidget,
	{
		generalText: string;
		tooltipText?: string;
		getObjectiveText: (obj: {
			closingDate?: string | Date;
			responsePercentage?: number;
			totalCounts?: number;
			staticReplacementText?: string;
			locations?: string[];
		}) => string;
	}
> = {
	LOCATION: {
		generalText: 'Location',
		tooltipText: COPY.formSettings.widgetLocationTip,
		getObjectiveText: ({ locations }) => {
			if (!locations || locations.length === 0) return 'Add Location';
			return locations.join(', ');
		},
	},
	RESPONSE_PERCENTAGE: {
		generalText: 'Responded to X% candidates',
		tooltipText: COPY.formSettings.widgetResponseRateTip(FORM_WIDGET_RESPONSE_RATE_THRESHOLD),
		getObjectiveText: ({ staticReplacementText, responsePercentage }) => {
			let replacement = staticReplacementText;

			if (!replacement)
				replacement =
					Number(responsePercentage) >= FORM_WIDGET_RESPONSE_RATE_THRESHOLD
						? Math.floor(responsePercentage ?? 0) === 100
							? '100'
							: (responsePercentage?.toFixed(0) ?? '100')
						: '100';

			return `Responded to ${replacement}% candidates`;
		},
	},
	CLOSING_DATE: {
		generalText: 'Apply by X',
		getObjectiveText: ({ closingDate, staticReplacementText }) => {
			let replacement = staticReplacementText;

			if (!replacement) {
				const dateToSet = new Date();
				dateToSet.setDate(dateToSet.getDate() + 7);
				dateToSet.setHours(23, 59, 59, 999);

				replacement = getDayMonthFromUTC(
					closingDate ? closingDate.toString() : dateToSet.toISOString()
				);
			}

			return `Apply by ${replacement}`;
		},
	},
	NUMBER_OF_APPLICANTS: {
		generalText: 'X Candidates',
		tooltipText: COPY.formSettings.widgetApplicantsTip(FORM_WIDGET_APPLICATIONS_THRESHOLD),
		getObjectiveText: ({ staticReplacementText, totalCounts }) => {
			let replacement = staticReplacementText;

			if (!replacement)
				replacement =
					Number(totalCounts) >= FORM_WIDGET_APPLICATIONS_THRESHOLD
						? (totalCounts?.toString() ?? 'X')
						: 'X';

			return `${replacement} Candidates`;
		},
	},
};

export const FORM_FIELD_TYPE_DISABLED_TOOLTIPS: Record<
	string,
	{
		isRequiredTooltip: string;
		typeTooltip: string;
		deleteTooltip: string;
	}
> = {
	NAME: {
		isRequiredTooltip: COPY.fields.lockedRequired('Name'),
		typeTooltip: COPY.fields.lockedType('name'),
		deleteTooltip: COPY.fields.lockedDelete('Name'),
	},
	EMAIL: {
		isRequiredTooltip: COPY.fields.lockedRequired('Email'),
		typeTooltip: COPY.fields.lockedType('email'),
		deleteTooltip: COPY.fields.lockedDelete('Email'),
	},
};

export const DEFAULT_FORM_TITLE = 'Untitled Form';

export const DEFAULT_SUCCESS_MESSAGE = 'We have received your submission.';
